import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { navigate, useCurrentPath } from '@/app/navigation';

interface Partner {
  id: string;
  name: string;
  email: string;
  code: string;
  rate6m: string;
  rateNext6m: string;
  createdAt: string;
  referrals: Referral[];
}

interface Referral {
  id: string;
  partnerId: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  status: string;
  contractStart: string | null;
  monthlyAmount: string | null;
  notes: string | null;
  createdAt: string;
}

interface Payout {
  id: string;
  partnerId: string;
  referralId: string;
  period: string;
  amount: string;
  status: string;
  paidAt: string | null;
  referral: { clientName: string };
}

const STATUS_LABELS: Record<string, string> = {
  interested: 'Interested',
  contracted: 'Client',
  paused: 'Paused',
  churned: 'Churned',
};

const STATUS_COLORS: Record<string, string> = {
  interested: 'bg-blue-100 text-blue-800',
  contracted: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  churned: 'bg-gray-100 text-gray-600',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[status] ?? 'bg-gray-100'}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function PartnerDetailPage() {
  const path = useCurrentPath();
  const partnerId = path.split('/').pop()!;
  const qc = useQueryClient();
  const [showAddLead, setShowAddLead] = useState(false);
  const [leadForm, setLeadForm] = useState({ clientName: '', clientEmail: '', clientPhone: '', notes: '' });
  const [editingReferral, setEditingReferral] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ status: string; contractStart: string; monthlyAmount: string; notes: string }>({ status: '', contractStart: '', monthlyAmount: '', notes: '' });
  const [calcPeriod, setCalcPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partners', partnerId],
    queryFn: () => api.get<Partner>(`/api/partners/${partnerId}`),
  });

  const { data: payouts = [] } = useQuery({
    queryKey: ['partner-payouts', partnerId],
    queryFn: () => api.get<Payout[]>(`/api/partners/payouts?partnerId=${partnerId}`),
  });

  const addLeadMutation = useMutation({
    mutationFn: (data: typeof leadForm) => api.post(`/api/partners/${partnerId}/referrals`, {
      clientName: data.clientName,
      clientEmail: data.clientEmail || undefined,
      clientPhone: data.clientPhone || undefined,
      notes: data.notes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners', partnerId] });
      setShowAddLead(false);
      setLeadForm({ clientName: '', clientEmail: '', clientPhone: '', notes: '' });
    },
  });

  const updateReferralMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/api/partners/referrals/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners', partnerId] });
      setEditingReferral(null);
    },
  });

  const calculateMutation = useMutation({
    mutationFn: () => api.post('/api/partners/payouts/calculate', { period: calcPeriod }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner-payouts', partnerId] }),
  });

  const markPaidMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/partners/payouts/${id}/paid`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner-payouts', partnerId] }),
  });

  if (isLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!partner) return <p className="text-destructive">Partner not found.</p>;

  const refLink = `https://partners.octery.com/ref/${partner.code}`;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/partners')} className="text-sm text-muted-foreground hover:underline">
        ← Back to partners
      </button>

      {/* Partner header */}
      <Card>
        <CardContent className="py-4 flex flex-wrap gap-6 items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{partner.name}</h1>
            <p className="text-muted-foreground text-sm">{partner.email}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Commission: {Math.round(parseFloat(partner.rate6m) * 100)}% (6m) → {Math.round(parseFloat(partner.rateNext6m) * 100)}% (next 6m)
            </p>
          </div>
          <div className="text-sm">
            <p className="text-muted-foreground mb-1">Referral link:</p>
            <div className="flex items-center gap-2">
              <code className="bg-muted px-2 py-1 rounded text-xs">{refLink}</code>
              <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(refLink)}>
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads / referrals */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Leads</CardTitle>
          <Button size="sm" onClick={() => setShowAddLead(!showAddLead)}>Add Lead</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {showAddLead && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <p className="font-medium text-sm">New lead</p>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Name *" value={leadForm.clientName} onChange={(e) => setLeadForm((f) => ({ ...f, clientName: e.target.value }))} />
                <Input placeholder="Email" value={leadForm.clientEmail} onChange={(e) => setLeadForm((f) => ({ ...f, clientEmail: e.target.value }))} />
                <Input placeholder="Phone" value={leadForm.clientPhone} onChange={(e) => setLeadForm((f) => ({ ...f, clientPhone: e.target.value }))} />
                <Input placeholder="Notes" value={leadForm.notes} onChange={(e) => setLeadForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => addLeadMutation.mutate(leadForm)} disabled={!leadForm.clientName || addLeadMutation.isPending}>
                  {addLeadMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddLead(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {partner.referrals.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">No leads yet.</p>
          ) : (
            partner.referrals.map((r) => (
              <div key={r.id} className="border rounded-lg overflow-hidden">
                <div
                  className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-muted/30"
                  onClick={() => {
                    if (editingReferral === r.id) {
                      setEditingReferral(null);
                    } else {
                      setEditingReferral(r.id);
                      setEditForm({
                        status: r.status,
                        contractStart: r.contractStart ?? '',
                        monthlyAmount: r.monthlyAmount ?? '',
                        notes: r.notes ?? '',
                      });
                    }
                  }}
                >
                  <div>
                    <span className="font-medium">{r.clientName}</span>
                    {r.clientEmail && <span className="text-sm text-muted-foreground ml-2">{r.clientEmail}</span>}
                    {r.contractStart && r.monthlyAmount && (
                      <p className="text-xs text-muted-foreground">Since {r.contractStart} · {r.monthlyAmount} ₽/mo</p>
                    )}
                  </div>
                  <StatusBadge status={r.status} />
                </div>

                {editingReferral === r.id && (
                  <div className="border-t px-4 py-3 space-y-3 bg-muted/20">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Status</label>
                        <select
                          className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-background"
                          value={editForm.status}
                          onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                        >
                          <option value="interested">Interested</option>
                          <option value="contracted">Contracted</option>
                          <option value="paused">Paused</option>
                          <option value="churned">Churned</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Contract start</label>
                        <Input type="date" className="mt-1" value={editForm.contractStart} onChange={(e) => setEditForm((f) => ({ ...f, contractStart: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Monthly amount</label>
                        <Input type="number" className="mt-1" placeholder="0" value={editForm.monthlyAmount} onChange={(e) => setEditForm((f) => ({ ...f, monthlyAmount: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Notes</label>
                        <Input className="mt-1" value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => updateReferralMutation.mutate({
                        id: r.id,
                        data: {
                          status: editForm.status,
                          contractStart: editForm.contractStart || undefined,
                          monthlyAmount: editForm.monthlyAmount ? parseFloat(editForm.monthlyAmount) : undefined,
                          notes: editForm.notes || undefined,
                        }
                      })} disabled={updateReferralMutation.isPending}>
                        {updateReferralMutation.isPending ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingReferral(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Payouts */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Payouts</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              type="month"
              className="w-40"
              value={calcPeriod}
              onChange={(e) => setCalcPeriod(e.target.value)}
            />
            <Button size="sm" onClick={() => calculateMutation.mutate()} disabled={calculateMutation.isPending}>
              {calculateMutation.isPending ? 'Calculating...' : 'Calculate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">No payouts yet. Click Calculate to generate.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="pb-2">Period</th>
                  <th className="pb-2">Client</th>
                  <th className="pb-2">Amount</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2">{p.period}</td>
                    <td className="py-2">{p.referral.clientName}</td>
                    <td className="py-2 font-medium">{parseFloat(p.amount).toLocaleString()} ₽</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      {p.status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => markPaidMutation.mutate(p.id)} disabled={markPaidMutation.isPending}>
                          Mark paid
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
